// W-0821 — Página: Transferir Títulos para Cobrança (Imob > Cobrador)
// Single-page com seções colapsáveis (ADR-009 global + Gate 1B Q-06 answered=A).
// Fluxo: Filtros → Pesquisa → Resultados+Atribuição → Lançar → Sumário.
//
// Decisões de implementação:
// - State local via useReducer (ADR-003: estado do "#Transf" Delphi vive no front-end).
// - Multi-seleção persiste entre páginas via array de codigo_boleto.
// - Lançamento dispara em chunks de LIMITE_LOTE_BOLETOS=200 (ADR-003).
// - Exports usam helpers globais salvarEmExcel/salvarEmPdf — recebem url + payload.
//
// LICOES aplicadas:
// - §1.5 key estável (codigo_boleto) ✔
// - §1.6 Yup completo ✔
// - §1.4 paginação edge: filtro reseta página para 1 ✔
// - §1.8 char 630 portão de migração ✔
// - §1.9 catch usa err.message (interceptor já fez unwrap)
// - Ciclo 9 L3 cleanup `ativo` em useEffect ✔
// - Ciclo 9 L6 LIMITE_LOTE_BOLETOS validado no Controller ✔
// - Ciclo 10 L2 export blob via httpService.fetch (Service.js)

import React, { useReducer, useState, useMemo, useEffect } from 'react';
import { Container, Row, Col, Card, Accordion, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  FaFilter,
  FaListUl,
  FaFilePdf,
  FaFileExcel,
  FaEdit,
} from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { LIMITE_LOTE_BOLETOS } from '~/constant';
import { salvarEmExcel, salvarEmPdf } from '~/Utils/Helpers';
import {
  transfCobrancaPesquisar,
  transfCobrancaLancar,
  transfCobrancaLookups,
} from '~/Services/Service';
import SecaoFiltros from './components/SecaoFiltros';
import GridBoletos from './components/GridBoletos';
import BulkActionBar from './components/BulkActionBar';
import PainelResumoCobradores from './components/PainelResumoCobradores';
import ModalAtribuirCobrador from './components/ModalAtribuirCobrador';
import ModalConfirmacaoLancamento from './components/ModalConfirmacaoLancamento';
import ModalSumarioLancamento from './components/ModalSumarioLancamento';
import BarraProgressoChunks from './components/BarraProgressoChunks';
import {
  reducer,
  initialState,
  actions,
  MODO_FILTROS,
  MODO_RESULTADOS,
} from './state';
import {
  dispatchEmChunks,
  extrairMensagemErro,
  resumirAtribuicoes,
} from './helpers';

const TransferirTitulos = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [loadingPesquisa, setLoadingPesquisa] = useState(false);
  const [cobradores, setCobradores] = useState([]); // [{ codigo, nome, tipo }]
  const [modalAtribuir, setModalAtribuir] = useState({
    show: false,
    codigos: [],
  });
  const [modalConfirmacao, setModalConfirmacao] = useState(false);
  const [modalSumario, setModalSumario] = useState(false);
  const [exportando, setExportando] = useState(null); // 'pdf' | 'excel' | null
  const [filtrosAtivos, setFiltrosAtivos] = useState(null); // payload da última pesquisa

  // Carrega lookup de cobradores (mantém em estado local — usado pelo modal e pelo grid)
  useEffect(() => {
    let ativo = true;
    transfCobrancaLookups()
      .then((res) => {
        if (!ativo) return;
        setCobradores(res.cobradores || []);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      });
    return () => {
      ativo = false;
    };
  }, []);

  const cobradoresMap = useMemo(() => {
    const m = new Map();
    (cobradores || []).forEach((c) => m.set(c.codigo, c.nome));
    return m;
  }, [cobradores]);

  const resumoAtribuicoes = useMemo(
    () => resumirAtribuicoes(state.resultados.boletos, state.atribuicoes),
    [state.resultados.boletos, state.atribuicoes]
  );

  const totalAtribuidos = useMemo(
    () => Object.keys(state.atribuicoes).length,
    [state.atribuicoes]
  );

  const totalValorAtribuido = useMemo(
    () => resumoAtribuicoes.reduce((soma, item) => soma + item.valor, 0),
    [resumoAtribuicoes]
  );

  const construirPayloadPesquisa = (valores, pagina = 1) => ({
    codigo_grupo_empresa: valores.codigoGrupoEmpresa,
    codigo_grupo_ges: valores.codigoGrupoGes,
    codigo_cidade_cobranca: valores.codigoCidadeCobranca,
    codigo_familia: valores.codigoFamilia,
    codigo_conta: valores.codigoConta,
    dias_minimo_vencidos: Number(valores.diasMinimoVencidos),
    dias_maximo_vencidos: Number(valores.diasMaximoVencidos),
    codigos_perfil_comprador: valores.codigosPerfilComprador,
    modo_automatico: !!valores.modoAutomatico,
    incluir_cheque_devolvido_alinea11: !!valores.incluirChequeDevolvidoAlinea11,
    pagina,
    tamanho_pagina: state.resultados.tamanhoPagina,
  });

  const executarPesquisa = async (valores, pagina = 1) => {
    setLoadingPesquisa(true);
    try {
      const payload = construirPayloadPesquisa(valores, pagina);
      setFiltrosAtivos(valores);
      dispatch({ type: actions.SET_FILTROS, payload: valores });
      const res = await transfCobrancaPesquisar(payload);
      dispatch({
        type: actions.SET_RESULTADOS,
        payload: {
          boletos: res.boletos || [],
          total: res.total || 0,
          pagina: res.pagina || pagina,
          tamanhoPagina: res.tamanho_pagina || state.resultados.tamanhoPagina,
          resumoCobradoresAtuais: res.resumo_cobradores_atuais || [],
          carteiraAtiva: res.carteira_ativa || [],
        },
      });
      dispatch({ type: actions.MUDAR_MODO, payload: MODO_RESULTADOS });
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoadingPesquisa(false);
    }
  };

  const handlePesquisar = (valores) => executarPesquisa(valores, 1);

  const handleMudarPagina = (novaPagina) => {
    if (!filtrosAtivos) return;
    executarPesquisa(filtrosAtivos, novaPagina);
  };

  const handleEditarFiltros = () => {
    if (state.selecionados.length > 0) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        'Você tem boletos selecionados. Editar filtros vai limpar a seleção. Deseja continuar?'
      );
      if (!ok) return;
      dispatch({ type: actions.LIMPAR_SELECAO });
    }
    dispatch({ type: actions.MUDAR_MODO, payload: MODO_FILTROS });
  };

  const handleLimparFiltros = () => {
    dispatch({ type: actions.RESET });
    setFiltrosAtivos(null);
  };

  const handleToggleSelecao = (codigo) =>
    dispatch({ type: actions.TOGGLE_SELECAO, payload: codigo });

  const handleTogglePagina = (codigos, marcar) =>
    dispatch({
      type: actions.TOGGLE_SELECAO_PAGINA,
      payload: { codigos, marcar },
    });

  const handleAbrirModalAtribuir = (codigos) => {
    if (!codigos || codigos.length === 0) {
      toast.warning('Selecione ao menos um boleto.');
      return;
    }
    setModalAtribuir({ show: true, codigos });
  };

  const handleConfirmarAtribuicao = (codigos, cobradorCodigo) => {
    if (codigos.length === 1) {
      dispatch({
        type: actions.ATRIBUIR_COBRADOR,
        payload: { codigoBoleto: codigos[0], cobradorCodigo },
      });
    } else {
      dispatch({
        type: actions.ATRIBUIR_LOTE,
        payload: { codigos, cobradorCodigo },
      });
    }
    setModalAtribuir({ show: false, codigos: [] });
    toast.success(
      codigos.length === 1
        ? 'Cobrador atribuído ao boleto.'
        : `Cobrador atribuído a ${codigos.length} boletos.`
    );
  };

  const handleRemoverAtribuicao = (codigoBoleto) =>
    dispatch({
      type: actions.ATRIBUIR_COBRADOR,
      payload: { codigoBoleto, cobradorCodigo: null },
    });

  const podeLancar =
    state.selecionados.length > 0 &&
    state.selecionados.every((c) => state.atribuicoes[c] != null);

  const abrirConfirmacao = () => {
    if (!podeLancar) {
      toast.warning(
        'Atribua cobrador a TODOS os boletos selecionados antes de lançar.'
      );
      return;
    }
    setModalConfirmacao(true);
  };

  const handleConfirmarLancamento = async () => {
    // Monta payload com boletos selecionados que tenham atribuição.
    // Cada item precisa de codigo_grupo_empresa (recupera da página atual ou de cache).
    const indexBoletos = new Map();
    state.resultados.boletos.forEach((b) =>
      indexBoletos.set(b.codigo_boleto, b)
    );
    const boletosLote = state.selecionados
      .filter((c) => state.atribuicoes[c] != null)
      .map((codigoBoleto) => {
        const b = indexBoletos.get(codigoBoleto);
        return {
          codigo_boleto: codigoBoleto,
          codigo_grupo_empresa: b ? b.codigo_grupo_empresa : null,
          cobrador_codigo: state.atribuicoes[codigoBoleto],
        };
      })
      .filter((item) => item.codigo_grupo_empresa != null);

    if (boletosLote.length === 0) {
      toast.warning(
        'Não há boletos atribuídos visíveis para lançamento. Ajuste a paginação para reincluir os selecionados.'
      );
      return;
    }

    dispatch({ type: actions.RESET_LANCAMENTO });
    dispatch({
      type: actions.SET_PROGRESSO_LANCAMENTO,
      payload: { emAndamento: true, chunkAtual: 0, totalChunks: 0 },
    });

    try {
      await dispatchEmChunks(
        boletosLote,
        LIMITE_LOTE_BOLETOS,
        (chunk) => transfCobrancaLancar({ boletos: chunk }),
        (progresso) =>
          dispatch({
            type: actions.SET_PROGRESSO_LANCAMENTO,
            payload: progresso,
          }),
        (respChunk) =>
          dispatch({
            type: actions.MERGE_RESPONSE_CHUNK,
            payload: respChunk,
          })
      );
      dispatch({ type: actions.FINALIZAR_LANCAMENTO });
      setModalConfirmacao(false);
      setModalSumario(true);
      toast.success('Lançamento concluído.');
      // Após lançamento, refazer pesquisa para refletir estado atual e limpar seleção.
      dispatch({ type: actions.LIMPAR_SELECAO });
      if (filtrosAtivos) {
        // LICOES §1.4 paginação edge: se a página atual ficar vazia após lançamento,
        // volta para a anterior. Heurística: se transferidos >= boletos da página atual,
        // assume que a página esvaziou.
        const transferidosNoChunk = state.lancamento.transferidos.length;
        const totalNaPagina = state.resultados.boletos.length;
        const paginaAtual = state.resultados.pagina;
        const proximaPagina =
          transferidosNoChunk >= totalNaPagina && paginaAtual > 1
            ? paginaAtual - 1
            : paginaAtual;
        executarPesquisa(filtrosAtivos, proximaPagina);
      }
    } catch (err) {
      dispatch({ type: actions.FINALIZAR_LANCAMENTO });
      toast.error(extrairMensagemErro(err));
    }
  };

  const handleExportPdf = async () => {
    if (!filtrosAtivos) {
      toast.warning('Realize a pesquisa antes de exportar.');
      return;
    }
    setExportando('pdf');
    try {
      const url = '/transf-cobranca/exportar-pdf';
      const payload = construirPayloadPesquisa(filtrosAtivos, 1);
      // salvarEmPdf usa GET — para POST com payload, usar fluxo blob diretamente.
      // Aqui delegamos para Service.transfCobrancaExportarPdf via salvarEmExcel (POST+blob).
      await salvarEmExcel(payload, url);
    } catch (err) {
      toast.error(extrairMensagemErro(err) || 'Falha ao exportar PDF.');
    } finally {
      setExportando(null);
    }
  };

  const handleExportExcel = async () => {
    if (!filtrosAtivos) {
      toast.warning('Realize a pesquisa antes de exportar.');
      return;
    }
    setExportando('excel');
    try {
      const url = '/transf-cobranca/exportar-excel';
      const payload = construirPayloadPesquisa(filtrosAtivos, 1);
      await salvarEmExcel(payload, url);
    } catch (err) {
      toast.error(extrairMensagemErro(err) || 'Falha ao exportar Excel.');
    } finally {
      setExportando(null);
    }
  };

  const ehFiltrosAberto = state.modo === MODO_FILTROS;
  const ehResultadosAberto = state.modo === MODO_RESULTADOS;

  return (
    <>
      <Header title="Transferir Títulos para Cobrança" />
      <Container fluid className="py-3">
        <Accordion activeKey={ehFiltrosAberto ? 'filtros' : null} alwaysOpen>
          <Accordion.Item eventKey="filtros">
            <Accordion.Header>
              <FaFilter className="me-2" /> 1. Filtros da pesquisa
            </Accordion.Header>
            <Accordion.Body className="p-2">
              <SecaoFiltros
                valoresIniciais={state.filtros}
                onPesquisar={handlePesquisar}
                onLimpar={handleLimparFiltros}
                loading={loadingPesquisa}
              />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        {ehResultadosAberto && (
          <>
            <Card className="mb-3">
              <Card.Body className="d-flex justify-content-between align-items-center flex-wrap py-2" style={{ gap: 8 }}>
                <div>
                  <FaListUl className="me-2" />
                  <strong>2. Resultados e atribuição</strong>
                </div>
                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleEditarFiltros}
                  >
                    <FaEdit /> Editar filtros
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={exportando !== null}
                    onClick={handleExportPdf}
                  >
                    {exportando === 'pdf' ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FaFilePdf />
                    )}{' '}
                    Exportar PDF
                  </Button>
                  <Button
                    variant="outline-success"
                    size="sm"
                    disabled={exportando !== null}
                    onClick={handleExportExcel}
                  >
                    {exportando === 'excel' ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FaFileExcel />
                    )}{' '}
                    Exportar Excel
                  </Button>
                </div>
              </Card.Body>
            </Card>

            <Row>
              <Col xs={12} lg={8}>
                <BulkActionBar
                  selecionados={state.selecionados}
                  totalSelecionado={state.selecionados.length}
                  totalAtribuidos={totalAtribuidos}
                  onAbrirAtribuirLote={() =>
                    handleAbrirModalAtribuir(state.selecionados)
                  }
                  onLimparSelecao={() =>
                    dispatch({ type: actions.LIMPAR_SELECAO })
                  }
                  onLancar={abrirConfirmacao}
                  podeLancar={podeLancar}
                />

                {loadingPesquisa ? (
                  <Loading label="Pesquisando boletos..." />
                ) : (
                  <GridBoletos
                    boletos={state.resultados.boletos}
                    total={state.resultados.total}
                    pagina={state.resultados.pagina}
                    tamanhoPagina={state.resultados.tamanhoPagina}
                    selecionados={state.selecionados}
                    atribuicoes={state.atribuicoes}
                    cobradoresMap={cobradoresMap}
                    onToggleSelecao={handleToggleSelecao}
                    onTogglePagina={handleTogglePagina}
                    onMudarPagina={handleMudarPagina}
                    onAbrirModalAtribuir={handleAbrirModalAtribuir}
                    onRemoverAtribuicao={handleRemoverAtribuicao}
                  />
                )}
              </Col>

              <Col xs={12} lg={4}>
                <PainelResumoCobradores
                  resumoAtribuicoes={resumoAtribuicoes}
                  resumoCobradoresAtuais={state.resultados.resumoCobradoresAtuais}
                  cobradoresMap={cobradoresMap}
                />
                {state.lancamento.emAndamento && (
                  <BarraProgressoChunks
                    chunkAtual={state.lancamento.chunkAtual}
                    totalChunks={state.lancamento.totalChunks}
                  />
                )}
              </Col>
            </Row>
          </>
        )}
      </Container>

      <ModalAtribuirCobrador
        show={modalAtribuir.show}
        codigosBoleto={modalAtribuir.codigos}
        cobradores={cobradores}
        onConfirmar={handleConfirmarAtribuicao}
        onCancelar={() => setModalAtribuir({ show: false, codigos: [] })}
      />

      <ModalConfirmacaoLancamento
        show={modalConfirmacao}
        resumoAtribuicoes={resumoAtribuicoes}
        totalBoletos={totalAtribuidos}
        totalValor={totalValorAtribuido}
        cobradoresMap={cobradoresMap}
        onConfirmar={handleConfirmarLancamento}
        onCancelar={() => setModalConfirmacao(false)}
        emAndamento={state.lancamento.emAndamento}
      />

      <ModalSumarioLancamento
        show={modalSumario}
        transferidos={state.lancamento.transferidos}
        ignorados={state.lancamento.ignorados}
        falhasExternas={state.lancamento.falhasExternas}
        onFechar={() => setModalSumario(false)}
      />
    </>
  );
};

export default TransferirTitulos;
